import type { RoleEnum } from '@zeus/index'
import type { AccessRefusal } from '~/helpers/auth/access-refusal'
import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader, MailCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { Button } from '~/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { PasswordInput } from '~/components/ui/password-input'
import { GQL_LOGIN, GQL_REQUEST_MAGIC_LINK } from '~/gql/auth/auth'
import { accessRefusalOf, REFUSAL_MESSAGE_KEYS, REFUSAL_PARAM, refusalIn } from '~/helpers/auth/access-refusal'
import { isEmailIdentifier, loginIdentifierRule } from '~/helpers/auth/login-identifier'
import { useAuth } from '~/helpers/store/auth'
import { useLoginMode } from '~/pages/authentication/use-login-mode'
import { getDefaultRoute } from '~/routes/protected-loader'
import AuthLayout from './components/AuthLayout'
import LoginSSO from './LoginSSO'

/**
 * L'écran accueille deux entrées (#39) : mot de passe et magic link. Le
 * formulaire bascule d'un mode à l'autre plutôt que d'empiler deux champs
 * email, et `sentTo` mémorise l'adresse pour l'écran de confirmation.
 */

/**
 * Codes d'erreur que le callback SSO (#40) peut renvoyer dans l'URL. Un code
 * inconnu retombe sur un message générique : la liste sert à expliquer les
 * refus courants, pas à énumérer tous les échecs possibles.
 */
const SSO_ERROR_KEYS: Record<string, string> = {
  SSO_ERROR_DOMAIN_NOT_ALLOWED: 'sso.error.domainNotAllowed',
  SSO_ERROR_ACCOUNT_DISABLED: 'sso.error.accountDisabled',
  SSO_ERROR_EMAIL_ALREADY_USED: 'sso.error.emailAlreadyUsed',
  SSO_ERROR_DEMO_LEARNER_LIMIT: 'sso.error.demoLearnerLimit',
  SSO_ERROR_PROVIDER_UNAVAILABLE: 'sso.error.providerUnavailable',
}

function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { setPayload, setLoading } = useAuth()

  const loginSchema = z.object({
    // E-mail, ou pseudo d'un apprenant sans e-mail (#271).
    email: loginIdentifierRule({ required: t('input.required.message'), email: t('input.email.message') }),
    password: z.string().min(1, { message: t('input.password') }),
  })

  const magicSchema = z.object({
    email: z.string().email({ message: t('input.email.message') }),
  })

  type LoginFormValues = z.infer<typeof loginSchema>
  type MagicFormValues = z.infer<typeof magicSchema>

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const magicForm = useForm<MagicFormValues>({
    resolver: zodResolver(magicSchema),
    defaultValues: { email: '' },
  })

  const [loginGql, { loading }] = useMutation(GQL_LOGIN)
  const [requestMagicLink, { loading: magicLoading }] = useMutation(GQL_REQUEST_MAGIC_LINK)
  const [authFailed, setAuthFailed] = useState(false)
  // Compte désactivé ou org suspendue (#177) : à la connexion, ou rapporté par
  // `apollo.ts` qui a refermé une session en cours et renvoyé ici.
  const [refusal, setRefusal] = useState<AccessRefusal | null>(() => refusalIn(searchParams.get(REFUSAL_PARAM)))
  const [sentTo, setSentTo] = useState<string | null>(null)

  const onSubmit = async (values: LoginFormValues) => {
    setAuthFailed(false)
    setRefusal(null)
    try {
      const { data } = await loginGql({ variables: { identifier: values.email, password: values.password } })
      if (data?.login.__typename === 'UserInfo') {
        setPayload({
          firstName: data.login.firstName,
          lastName: data.login.lastName,
          email: data.login.email,
          username: data.login.username,
          role: data.login.roles,
          id: data.login.id,
        })
        setLoading(false)
        navigate(getDefaultRoute(data.login.roles as RoleEnum))
      } else {
        // CustomError (identifiants incorrects) — pas une exception (#97).
        setAuthFailed(true)
      }
    } catch (error) {
      const refused = accessRefusalOf(error)
      if (refused) setRefusal(refused)
      else toast.error(t('bad.server-login.message'))
    }
  }

  const onMagicSubmit = async (values: MagicFormValues) => {
    try {
      await requestMagicLink({ variables: { email: values.email } })
      // L'API répond `true` même pour une adresse inconnue (anti-énumération) :
      // l'écran de confirmation reste donc volontairement au conditionnel.
      setSentTo(values.email)
    } catch {
      toast.error(t('bad.server-login.message'))
    }
  }

  const { mode, switchMode } = useLoginMode({
    reset: () => {
      setAuthFailed(false)
      setSentTo(null)
    },
    carryEmail: (next) => {
      // Un pseudo n'a pas de lien magique : seul un e-mail passe d'un formulaire à l'autre.
      if (next === 'magic') magicForm.setValue('email', isEmailIdentifier(form.getValues('email')) ? form.getValues('email') : '')
      else form.setValue('email', magicForm.getValues('email'))
    },
    focus: (next) => {
      if (next === 'magic') magicForm.setFocus('email')
      else form.setFocus('email')
    },
  })

  const ssoError = searchParams.get('error')
  const ssoErrorMessage = ssoError
    ? t(SSO_ERROR_KEYS[ssoError] ?? 'sso.error.generic')
    : null

  return (
    <AuthLayout tagline={t('login.tagline')} taglineSub={t('login.tagline.sub')}>
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
        <h1 className="font-heading text-4xl font-extrabold tracking-tight">{t('common.login')}</h1>
        <p className="mt-2 mb-8 text-muted-foreground">
          {mode === 'password' ? t('common.login.description') : t('login.magic-link.description')}
        </p>

        {refusal && (
          <p role="alert" className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {t(REFUSAL_MESSAGE_KEYS[refusal])}
          </p>
        )}

        {ssoErrorMessage && (
          <p role="alert" className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {ssoErrorMessage}
          </p>
        )}

        {/* Région présente dès l'affichage : sans elle, l'envoi du lien n'est pas annoncé (RGAA 7.5). */}
        <p role="status" className="sr-only">
          {mode === 'magic' && sentTo ? `${t('login.magic-link.sent.title')} ${t('login.magic-link.sent.description', { email: sentTo })}` : ''}
        </p>

        {mode === 'magic' && sentTo && (
          <div className="flex flex-col">
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-muted/50 px-6 py-8 text-center">
              <MailCheck className="size-8 text-primary-text" />
              <p className="font-heading text-lg font-semibold">{t('login.magic-link.sent.title')}</p>
              <p className="text-sm text-muted-foreground">
                {t('login.magic-link.sent.description', { email: sentTo })}
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              disabled={magicLoading}
              onClick={() => onMagicSubmit({ email: sentTo })}
              className="mt-6 h-12 w-full rounded-full"
            >
              {magicLoading && <Loader className="mr-2 size-4 animate-spin" />}
              {t('login.magic-link.resend')}
            </Button>

            <button
              type="button"
              onClick={() => switchMode('password')}
              className="mt-6 text-center text-sm font-semibold text-primary-text underline underline-offset-4"
            >
              {t('login.password.cta')}
            </button>
          </div>
        )}

        {mode === 'magic' && !sentTo && (
          <Form {...magicForm}>
            <form onSubmit={magicForm.handleSubmit(onMagicSubmit)} className="flex flex-col">
              <RequiredFieldsHint />
              <FormField
                control={magicForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="mb-6">
                    <div className="grid gap-2">
                      <Label htmlFor="magic-email" required className="text-xs font-semibold text-muted-foreground">{t('common.email')}</Label>
                      <FormControl>
                        <Input
                          id="magic-email"
                          aria-required
                          autoComplete="email"
                          type="email"
                          placeholder="vous@exemple.com"
                          className="h-12 rounded-xl bg-muted"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={magicLoading} className="h-14 w-full rounded-full text-base font-semibold">
                {magicLoading && <Loader className="mr-2 size-4 animate-spin" />}
                {t('login.magic-link.submit')}
              </Button>

              <button
                type="button"
                onClick={() => switchMode('password')}
                className="mt-6 text-center text-sm font-semibold text-primary-text underline underline-offset-4"
              >
                {t('login.password.cta')}
              </button>
            </form>
          </Form>
        )}

        {mode === 'password' && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
              <RequiredFieldsHint />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="mb-4">
                    <div className="grid gap-2">
                      <Label htmlFor="email" required className="text-xs font-semibold text-muted-foreground">{t('login.identifier')}</Label>
                      <FormControl>
                        <Input
                          id="email"
                          aria-required
                          autoComplete="username"
                          autoCapitalize="none"
                          spellCheck={false}
                          type="text"
                          placeholder={t('login.identifier.placeholder')}
                          className="h-12 rounded-xl bg-muted"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="mb-2">
                    <div className="grid gap-2">
                      <Label htmlFor="password" required className="text-xs font-semibold text-muted-foreground">{t('common.password')}</Label>
                      <FormControl>
                        <PasswordInput
                          id="password"
                          aria-required
                          autoComplete="current-password"
                          placeholder="••••••••"
                          className="h-12 rounded-xl bg-muted"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <button
                type="button"
                onClick={() => navigate('/reset-password')}
                className="mb-6 ml-auto text-sm font-semibold text-primary-text underline underline-offset-4"
              >
                {t('password.forgot')}
              </button>
              <p className="-mt-4 mb-6 text-right text-xs text-muted-foreground">{t('login.username.forgot')}</p>

              {authFailed && (
                <p role="alert" className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {t('bad.connection.message')}
                </p>
              )}

              <Button type="submit" disabled={loading} className="h-14 w-full rounded-full text-base font-semibold">
                {loading && <Loader className="mr-2 size-4 animate-spin" />}
                {t('common.login')}
              </Button>

              <div className="my-6 flex items-center gap-3 text-sm text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                {t('common.or')}
                <span className="h-px flex-1 bg-border" />
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={() => switchMode('magic')}
                className="mb-3 h-12 w-full rounded-full"
              >
                {t('login.magic-link.cta')}
              </Button>

              <LoginSSO />
            </form>
          </Form>
        )}
      </div>
    </AuthLayout>
  )
}

export default Login
